import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import api from '../utils/api';
import { useToast, Modal, InlineSpinner } from './UI';

const MAX_SIZE = 10 * 1024 * 1024;

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isImage(mimeType) {
  return typeof mimeType === 'string' && mimeType.startsWith('image/');
}

// Reusable attachment widget for add/edit forms.
// Files picked here are only queued locally and are NOT sent to the server as
// you pick them — the parent form must call ref.flush(recordId) after the
// record is actually saved (Create/Update clicked), so nothing is persisted
// if the user picks a file and then cancels out of the form.
const FileAttachments = forwardRef(function FileAttachments({ module, recordId, canUpload, canDelete }, ref) {
  const toast = useToast();
  const [files, setFiles]       = useState([]);
  const [queue, setQueue]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]   = useState(null); // { url, name }
  const [downloadingAll, setDownloadingAll] = useState(false);

  const load = () => {
    if (!recordId) return;
    setLoading(true);
    api.get(`/attachments/${module}/${recordId}`)
      .then(r => setFiles(r.data.data))
      .catch(() => toast('Failed to load attachments', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [module, recordId]);

  useImperativeHandle(ref, () => ({
    hasQueued: () => queue.length > 0,
    flush: async (targetRecordId) => {
      const id = targetRecordId ?? recordId;
      if (!queue.length || !id) return;
      setUploading(true);
      for (const f of queue) {
        const fd = new FormData(); fd.append('file', f);
        try {
          await api.post(`/attachments/${module}/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) {
          toast(`Failed to attach "${f.name}": ${err.response?.data?.message || 'upload failed'}`, 'error');
        }
      }
      setQueue([]);
      setUploading(false);
      if (id === recordId) load();
    },
  }));

  const handlePick = e => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const tooBig = picked.filter(f => f.size > MAX_SIZE);
    if (tooBig.length) toast(`${tooBig.map(f => f.name).join(', ')} exceeds the 10MB per-file limit`, 'error');
    const ok = picked.filter(f => f.size <= MAX_SIZE);
    if (!ok.length) return;
    setQueue(q => [...q, ...ok]);
  };

  const handleView = async (file) => {
    try {
      const res = await api.get(`/attachments/file/${file.id}`, { responseType: 'blob' });
      setPreview({ url: URL.createObjectURL(res.data), name: file.original_name });
    } catch {
      toast('Failed to open file', 'error');
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handleDownload = async (file) => {
    try {
      const res = await api.get(`/attachments/file/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = file.original_name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast(`Failed to download "${file.original_name}"`, 'error');
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    for (const f of files) {
      await handleDownload(f);
      await new Promise(r => setTimeout(r, 300)); // stagger — browsers block rapid-fire multi-downloads
    }
    setDownloadingAll(false);
  };

  const handleDelete = async (file) => {
    try {
      await api.delete(`/attachments/file/${file.id}`);
      toast('File removed', 'success');
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Delete failed', 'error');
    }
  };

  const removeQueued = (idx) => setQueue(q => q.filter((_, i) => i !== idx));

  return (
    <div className="sms-attachments">
      <div className="d-flex justify-content-between align-items-center mb-2" style={{ flexWrap: 'wrap', gap: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)' }}>Attachments</label>
        <div className="d-flex" style={{ gap: 8, flexWrap: 'wrap' }}>
          {files.length > 0 && (
            <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12 }} onClick={handleDownloadAll} disabled={downloadingAll}>
              {downloadingAll ? <InlineSpinner /> : <i className="bi bi-download" />} Download all
            </button>
          )}
          {canUpload && (
            <label className="sms-btn sms-btn-outline" style={{ fontSize: 12, cursor: 'pointer', marginBottom: 0 }}>
              {uploading ? <InlineSpinner /> : <i className="bi bi-paperclip" />} Add file
              <input type="file" multiple style={{ display: 'none' }} onChange={handlePick} />
            </label>
          )}
        </div>
      </div>

      {queue.length === 0 && !recordId && (
        <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>No files attached yet. Files you add will be saved together with this record.</div>
      )}
      {recordId && loading && <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>Loading…</div>}
      {recordId && !loading && files.length === 0 && queue.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>No files attached.</div>
      )}
      {queue.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginBottom: 6 }}>
          Files below will be uploaded when you {recordId ? 'click Update' : 'save this record'}.
        </div>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {queue.map((f, i) => (
          <li key={`q-${i}`} className="d-flex align-items-center justify-content-between" style={{ fontSize: 12, background: 'var(--sms-sur2)', borderRadius: 6, padding: '6px 10px', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '1 1 160px' }}>
              <i className="bi bi-file-earmark" /> {f.name} <span style={{ color: 'var(--sms-txt3)' }}>({formatSize(f.size)})</span>
            </span>
            <button type="button" className="sms-btn sms-btn-outline-danger" style={{ padding: '5px', flexShrink: 0 }} onClick={() => removeQueued(i)} aria-label={`Remove ${f.name}`} title="Remove">
              <i className="bi bi-x-lg" style={{ fontSize: 12 }} />
            </button>
          </li>
        ))}
        {files.map(f => (
          <li key={f.id} className="d-flex align-items-center justify-content-between" style={{ fontSize: 12, background: 'var(--sms-sur2)', borderRadius: 6, padding: '6px 10px', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '1 1 160px' }}>
              <i className="bi bi-file-earmark" /> {f.original_name} <span style={{ color: 'var(--sms-txt3)' }}>({formatSize(f.size_bytes)})</span>
            </span>
            <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
              {isImage(f.mime_type) && (
                <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '5px' }} onClick={() => handleView(f)} aria-label={`View ${f.original_name}`} title="View">
                  <i className="bi bi-eye" style={{ fontSize: 12 }} />
                </button>
              )}
              <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '5px' }} onClick={() => handleDownload(f)} aria-label={`Download ${f.original_name}`} title="Download">
                <i className="bi bi-download" style={{ fontSize: 12 }} />
              </button>
              {canDelete && (
                <button type="button" className="sms-btn sms-btn-outline-danger" style={{ padding: '5px' }} onClick={() => handleDelete(f)} aria-label={`Delete ${f.original_name}`} title="Delete">
                  <i className="bi bi-trash" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Modal show={!!preview} onClose={closePreview} title={preview?.name} size="lg">
        {preview && (
          <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }} />
        )}
      </Modal>
    </div>
  );
});

export default FileAttachments;
